import { TextInput } from '@mantine/core';
import useAiAnalystContext from '@providers/AiAnalyst/useAiAnalystContext';
import React, { useCallback, useEffect, useRef } from 'react';

interface ChatTitleInputProps {
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    onClick: () => void;
    isInputExpanded: boolean;
    isLoading: boolean;
    handleResetInput: () => void;
}

const ChatTitleInput: React.FC<ChatTitleInputProps> = ({
    value,
    onChange,
    onBlur,
    onClick,
    isInputExpanded,
    isLoading,
    handleResetInput,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { aiChatMessages } = useAiAnalystContext((context) => context.state);
    const hasMessages = aiChatMessages.length > 0;
    useEffect(() => {
        if (isInputExpanded && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isInputExpanded]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                onBlur();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                handleResetInput();
            }
        },
        [onBlur, handleResetInput],
    );

    if (!hasMessages) return null;

    if (isInputExpanded) {
        return (
            <TextInput
                ref={inputRef}
                value={value}
                onChange={(e) => onChange(e.currentTarget.value)}
                onBlur={onBlur}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                placeholder="Chat title"
                styles={{
                    input: {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        color: 'white',
                    },
                }}
            />
        );
    }

    return (
        <TextInput
            value={value || 'New chat'}
            onClick={onClick}
            readOnly
            disabled={isLoading}
            styles={{
                input: {
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                },
            }}
        />
    );
};

export default ChatTitleInput;
